/**
 * EventOperationsWorkspace — UEIM-05
 * Live operational execution workspace for CONFIRMED/LIVE events.
 * Route: /crm/events/:eid/operations
 *
 * 7 tabs: Control Tower | Run of Show | Streams | Partners | Team | Checkpoints | Incidents
 *
 * Architectural invariants enforced in UI:
 * - readiness_percent is derived (never manually entered)
 * - Go/No-Go staleness is surfaced with a re-evaluate prompt
 * - Go Live button guarded: CONFIRMED + ACTIVE session + non-stale GO/CONDITIONAL_GO gate
 * - No PO, no AP/AR/GL fields anywhere in this workspace
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Radio, CheckCircle2, XCircle, AlertTriangle, Clock,
  Plus, Loader2, RefreshCw, Users, Building2, Shield, Zap,
  ChevronRight, Activity, BarChart3, Target, TrendingUp, AlertCircle,
  CheckSquare, UserCheck, Siren, Layers, DollarSign, TrendingDown,
  ExternalLink, MessageSquare, PackageCheck,
} from "lucide-react";
import { opsApi, eventApi, eventFinanceApi, closeoutApi, opsSetupApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

const EVENT_STATUS_STYLE = {
  PLANNING: "bg-amber-100 text-amber-700 border-amber-200",
  CONFIRMED: "bg-indigo-100 text-indigo-700 border-indigo-200",
  LIVE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  COMPLETED: "bg-slate-100 text-slate-600",
  CLOSED: "bg-slate-100 text-slate-400",
  CANCELLED: "bg-red-100 text-red-500",
};
const GATE_STYLE = {
  GO: "bg-emerald-100 text-emerald-800 border-emerald-300",
  CONDITIONAL_GO: "bg-amber-100 text-amber-800 border-amber-300",
  NO_GO: "bg-red-100 text-red-800 border-red-300",
  NOT_EVALUATED: "bg-slate-100 text-slate-500 border-slate-200",
};
const GATE_LABEL = {
  GO: "GO",
  CONDITIONAL_GO: "CONDITIONAL GO",
  NO_GO: "NO-GO",
  NOT_EVALUATED: "NOT EVALUATED",
};
const SEV_STYLE = {
  LOW: "bg-slate-100 text-slate-600",
  MEDIUM: "bg-amber-100 text-amber-700",
  HIGH: "bg-orange-100 text-orange-700",
  CRITICAL: "bg-red-100 text-red-700 font-semibold",
};
const ROS_STATUS_STYLE = {
  SCHEDULED: "bg-slate-100 text-slate-600",
  READY: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-indigo-100 text-indigo-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  DELAYED: "bg-amber-100 text-amber-700",
  SKIPPED: "bg-slate-100 text-slate-400",
  CANCELLED: "bg-red-100 text-red-400",
};
const STREAM_STATUS_STYLE = {
  NOT_READY: "bg-slate-100 text-slate-500",
  READY: "bg-blue-100 text-blue-700",
  EXECUTING: "bg-indigo-100 text-indigo-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  BLOCKED: "bg-red-100 text-red-700 font-semibold",
  AT_RISK: "bg-amber-100 text-amber-700",
};
const CHK_STATUS_STYLE = {
  PENDING: "bg-slate-100 text-slate-500",
  IN_PROGRESS: "bg-blue-100 text-blue-700",
  VERIFIED: "bg-emerald-100 text-emerald-700",
  FAILED: "bg-red-100 text-red-700 font-semibold",
  WAIVED: "bg-amber-100 text-amber-600",
};
const INC_STATUS_STYLE = {
  OPEN: "bg-red-100 text-red-700",
  ACKNOWLEDGED: "bg-orange-100 text-orange-700",
  MITIGATING: "bg-amber-100 text-amber-700",
  RESOLVED: "bg-emerald-100 text-emerald-700",
  CLOSED: "bg-slate-100 text-slate-500",
};

const TABS = [
  { id: "tower",       label: "Control Tower",   icon: Radio },
  { id: "runofshow",   label: "Run of Show",      icon: Clock },
  { id: "streams",     label: "Streams",          icon: Zap },
  { id: "partners",    label: "Partners",         icon: Building2 },
  { id: "team",        label: "Team",             icon: Users },
  { id: "checkpoints", label: "Checkpoints",      icon: CheckSquare },
  { id: "incidents",   label: "Incidents",        icon: Siren },
];

// ── Root ─────────────────────────────────────────────────────────────────────
export default function EventOperationsWorkspace() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("tower");
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const canRead       = hasCapability(user, "event.ops.read");
  const canManage     = hasCapability(user, "event.ops.manage");
  const canRoS        = hasCapability(user, "event.ops.runofshow");
  const canCheckpoint = hasCapability(user, "event.ops.checkpoint.manage");
  const canPartner    = hasCapability(user, "event.ops.partner.checkin");
  const canTeam       = hasCapability(user, "event.ops.team.checkin");
  const canIncident   = hasCapability(user, "event.ops.incident.manage");
  const canGoLive     = hasCapability(user, "event.ops.go_live");
  const canOverride   = hasCapability(user, "event.ops.override");

  const loadEvent = useCallback(async () => {
    try {
      const res = await eventApi.getEvent(eid);
      setEvent(res.data);
    } catch { toast.error("Failed to load event"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );
  if (!event) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
      Event not found.
    </div>
  );
  if (!canRead) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
      You do not have access to Event Operations.
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(`/crm/events/${eid}`)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-event-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-emerald-600" />
                  <h1 className="font-bold text-slate-900 text-xl">{event.event_name}</h1>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${EVENT_STATUS_STYLE[event.status] || "bg-slate-100 text-slate-500"}`}>{event.status}</span>
                  <span className="text-xs text-slate-400 font-medium uppercase tracking-wide ml-1">Event Operations</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{event.primary_venue} · {fmtDate(event.event_start_at)}</p>
              </div>
            </div>
          </div>

          {/* Tab nav */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-0.5">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`ops-tab-${t.id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${
                    active ? "bg-emerald-50 text-emerald-700" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {tab === "tower"       && <ControlTowerTab eid={eid} event={event} canManage={canManage} canGoLive={canGoLive} canOverride={canOverride} onEventUpdate={loadEvent} />}
        {tab === "runofshow"   && <RunOfShowTab eid={eid} canRoS={canRoS} />}
        {tab === "streams"     && <StreamsTab eid={eid} canManage={canManage} />}
        {tab === "partners"    && <PartnersTab eid={eid} canPartner={canPartner} />}
        {tab === "team"        && <TeamTab eid={eid} canTeam={canTeam} />}
        {tab === "checkpoints" && <CheckpointsTab eid={eid} canCheckpoint={canCheckpoint} canOverride={canOverride} />}
        {tab === "incidents"   && <IncidentsTab eid={eid} canIncident={canIncident} />}
      </div>
    </div>
  );
}

// ── Control Tower Tab ─────────────────────────────────────────────────────────
function ControlTowerTab({ eid, event, canManage, canGoLive, canOverride, onEventUpdate }) {
  const navigate_ = useNavigate();
  const [tower, setTower] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [sessionForm, setSessionForm] = useState({ session_name: "", operations_lead_user_id: "", operations_start_at: "" });
  const [evaluating, setEvaluating] = useState(false);
  const [goingLive, setGoingLive] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await opsApi.getControlTower(eid);
      setTower(res.data);
    } catch { toast.error("Failed to load control tower"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const activateSession = async () => {
    const sessions = await opsApi.listSessions(eid);
    const notStarted = sessions.data.find(s => s.status === "NOT_STARTED");
    if (!notStarted) { toast.error("No NOT_STARTED session to activate. Create one first."); return; }
    try {
      await opsApi.transitionSession(eid, notStarted.id, { target_status: "ACTIVE" });
      toast.success("Session activated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate session"); }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await opsApi.createSession(eid, sessionForm);
      toast.success("Session created");
      setShowCreateSession(false);
      setSessionForm({ session_name: "", operations_lead_user_id: "", operations_start_at: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create session"); }
    finally { setCreating(false); }
  };

  const evaluateGate = async () => {
    setEvaluating(true);
    try {
      const res = await opsApi.evaluateGate(eid, { gate_type: "PRE_LIVE" });
      toast.success(`Gate evaluated: ${res.data.gate_status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Evaluation failed"); }
    finally { setEvaluating(false); }
  };

  const handleGoLive = async () => {
    if (!window.confirm("Transition this event to LIVE? This action is logged and cannot be undone.")) return;
    setGoingLive(true);
    try {
      await opsApi.goLive(eid, {});
      toast.success("Event is now LIVE");
      onEventUpdate();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Go-Live failed"); }
    finally { setGoingLive(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!tower) return null;

  const sess = tower.active_session;
  const gate = tower.gate_status;
  const stale = tower.gate_stale;
  const canGoLiveNow = canGoLive && event.status === "CONFIRMED" && sess && (gate === "GO" || gate === "CONDITIONAL_GO") && !stale;

  return (
    <div className="space-y-6">
      {/* Session + Gate header row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Active session */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-500" />Operations Session</h3>
            {canManage && !sess && (
              <Button size="sm" variant="outline" onClick={() => setShowCreateSession(true)} data-testid="create-session-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />New Session
              </Button>
            )}
            {canManage && sess && sess.status === "NOT_STARTED" && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={activateSession} data-testid="activate-session-btn">
                Activate
              </Button>
            )}
          </div>
          {sess ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">{sess.session_name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sess.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{sess.status}</span>
              </div>
              <p className="text-xs text-slate-500">Start: {fmt(sess.operations_start_at)}</p>
              {sess.operations_end_at && <p className="text-xs text-slate-500">Est. End: {fmt(sess.operations_end_at)}</p>}
              {sess.control_room_opened_at && <p className="text-xs text-slate-500">Control Room opened: {fmt(sess.control_room_opened_at)}</p>}
              <p className="text-xs text-slate-400 mt-1">State version: {sess.operations_state_version}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No active operations session. {canManage ? "Create one to begin." : ""}</p>
          )}

          {showCreateSession && (
            <form onSubmit={handleCreateSession} className="mt-4 border-t border-slate-100 pt-4 space-y-3">
              <div>
                <Label className="text-xs">Session Name *</Label>
                <Input placeholder="e.g. Conference Day 1" value={sessionForm.session_name} onChange={e => setSessionForm(p => ({...p, session_name: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="session-name-input" />
              </div>
              <div>
                <Label className="text-xs">Operations Lead (User ID) *</Label>
                <Input placeholder="user ID" value={sessionForm.operations_lead_user_id} onChange={e => setSessionForm(p => ({...p, operations_lead_user_id: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="session-lead-input" />
              </div>
              <div>
                <Label className="text-xs">Start At (ISO) *</Label>
                <Input type="datetime-local" value={sessionForm.operations_start_at} onChange={e => setSessionForm(p => ({...p, operations_start_at: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="session-start-input" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={creating} data-testid="session-create-submit">{creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Create Session</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowCreateSession(false)}>Cancel</Button>
              </div>
            </form>
          )}
        </div>

        {/* Go/No-Go Gate */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Target className="w-4 h-4 text-indigo-500" />Go / No-Go Gate</h3>
            <div className="flex gap-2">
              {canGoLive && sess && sess.status === "ACTIVE" && (
                <Button size="sm" variant="outline" onClick={evaluateGate} disabled={evaluating} data-testid="evaluate-gate-btn">
                  {evaluating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                  Evaluate
                </Button>
              )}
              {canGoLiveNow && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleGoLive} disabled={goingLive} data-testid="go-live-btn">
                  {goingLive ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                  Go Live
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${GATE_STYLE[gate] || GATE_STYLE.NOT_EVALUATED}`} data-testid="gate-status-badge">
              {GATE_LABEL[gate] || gate}
            </span>
            {stale && (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="gate-stale-badge">
                <AlertCircle className="w-3 h-3" />Stale — re-evaluate
              </span>
            )}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div>Checkpoints: <span className="font-medium">{tower.checkpoints.verified}/{tower.checkpoints.total}</span> verified</div>
            {tower.checkpoints.failed > 0 && <div className="text-red-600">Failed: {tower.checkpoints.failed}</div>}
            <div>Critical incidents: <span className={`font-medium ${tower.incidents.open_critical > 0 ? "text-red-600" : ""}`}>{tower.incidents.open_critical}</span></div>
            <div>Streams blocked: <span className={`font-medium ${tower.streams.blocked > 0 ? "text-red-600" : ""}`}>{tower.streams.blocked}</span></div>
          </div>
        </div>
      </div>

      {/* Attention Required */}
      {tower.attention_required?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Attention Required</h4>
          <div className="flex flex-wrap gap-2">
            {tower.attention_required.map((a, i) => (
              <span key={i} className="text-xs bg-white border border-amber-300 text-amber-800 px-2.5 py-1 rounded-full" data-testid={`attention-item-${a.type.toLowerCase()}`}>{a.label}</span>
            ))}
          </div>
        </div>
      )}

      {/* Health tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Streams",     ok: tower.streams.ready,      total: tower.streams.total,      warn: tower.streams.blocked,     icon: Zap,        c: "text-indigo-600 bg-indigo-50",  testid: "tile-streams" },
          { label: "Run of Show", ok: tower.run_of_show.completed, total: tower.run_of_show.total, warn: tower.run_of_show.delayed, icon: Clock,       c: "text-blue-600 bg-blue-50",     testid: "tile-ros" },
          { label: "Partners",    ok: tower.partners.arrived,   total: tower.partners.total,     warn: tower.partners.late + tower.partners.no_show, icon: Building2, c: "text-purple-600 bg-purple-50", testid: "tile-partners" },
          { label: "Team",        ok: tower.team.checked_in,    total: tower.team.total,         warn: 0,                         icon: Users,       c: "text-teal-600 bg-teal-50",     testid: "tile-team" },
          { label: "Checkpoints", ok: tower.checkpoints.verified, total: tower.checkpoints.total, warn: tower.checkpoints.failed, icon: CheckSquare, c: tower.checkpoints.failed > 0 ? "text-red-600 bg-red-50" : "text-emerald-600 bg-emerald-50", testid: "tile-checkpoints" },
          { label: "Incidents",   ok: 0,                        total: tower.incidents.total,    warn: tower.incidents.open_critical + tower.incidents.open_high, icon: Siren, c: (tower.incidents.open_critical + tower.incidents.open_high) > 0 ? "text-red-600 bg-red-50" : "text-slate-600 bg-slate-50", testid: "tile-incidents" },
        ].map(({ label, ok, total, warn, icon: Icon, c, testid }) => (
          <div key={label} data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-2 ${c}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="text-lg font-bold text-slate-900">{ok}/{total}</div>
            <div className="text-xs text-slate-500">{label}</div>
            {warn > 0 && <div className="text-xs text-red-500 mt-0.5">{warn} at risk</div>}
          </div>
        ))}
      </div>

      {/* E2E-04D: EFIN Financial Summary strip */}
      <EfinSummaryStrip eid={eid} />

      {/* E2E-04H: Supplemental Control Tower panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CloseoutReadinessPanel eid={eid} navigate={navigate_} />
        <AcceptanceSummaryPanel eid={eid} />
        <SourcingPackagePanel eid={eid} />
      </div>
    </div>
  );
}

// ── E2E-04D: EFIN Summary Strip ───────────────────────────────────────────────
function EfinSummaryStrip({ eid }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canViewFinance = hasCapability(user, "event.finance.view");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canViewFinance) { setLoading(false); return; }
    eventFinanceApi.getMetrics(eid)
      .then(r => setMetrics(r.data))
      .catch(() => setMetrics(null))
      .finally(() => setLoading(false));
  }, [eid, canViewFinance]);

  if (!canViewFinance) return null;

  function fmtMinor(v) {
    if (v == null) return "—";
    return `₹${(v / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  const ar = metrics?.ar_metrics || {};
  const ap = metrics?.ap_metrics || {};
  const drv = metrics?.derived_metrics || {};
  const invoicedRevenue = ar.invoiced_revenue_ex_tax_minor ?? ar.invoiced_revenue_minor;
  const committedCost   = ap.committed_cost_ex_tax_minor ?? ap.committed_cost_minor;
  const outstandingAR   = ar.outstanding_ar_minor;
  const outstandingAP   = ap.outstanding_ap_minor;
  const margin          = drv.gross_margin_minor;
  const marginPct       = drv.gross_margin_pct;
  const marginNeg       = (margin ?? 0) < 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="efin-summary-strip">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-violet-500" />Event Financial Summary
        </h3>
        <div className="flex items-center gap-2">
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
          ) : metrics ? (
            <span className="text-[10px] font-mono bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">READ-ONLY SNAPSHOT</span>
          ) : (
            <span className="text-[10px] font-mono text-slate-400">No financial data</span>
          )}
          <button
            onClick={() => navigate(`/crm/events/${eid}`, { state: { tab: "financials" } })}
            data-testid="efin-drill-through-btn"
            className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-0.5 font-medium border border-violet-200 px-2.5 py-1 rounded-lg hover:border-violet-400 transition-all"
          >
            <ExternalLink className="w-3 h-3 mr-0.5" />View Full Financials
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[1,2,3,4,5].map(i => <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />)}
        </div>
      ) : !metrics ? (
        <p className="text-sm text-slate-400 text-center py-3">Financial data unavailable or not yet attributed for this event.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Invoiced AR",    value: fmtMinor(invoicedRevenue), sub: "Revenue",       icon: TrendingUp,   color: "text-emerald-700", bg: "bg-emerald-50", testid: "efin-invoiced-ar" },
            { label: "Outstanding AR", value: fmtMinor(outstandingAR),   sub: "Uncollected",   icon: Clock,        color: "text-amber-700",   bg: "bg-amber-50",   testid: "efin-outstanding-ar" },
            { label: "Committed AP",   value: fmtMinor(committedCost),   sub: "Cost",          icon: TrendingDown, color: "text-blue-700",    bg: "bg-blue-50",    testid: "efin-committed-ap" },
            { label: "Outstanding AP", value: fmtMinor(outstandingAP),   sub: "Unpaid",        icon: PackageCheck, color: "text-orange-700",  bg: "bg-orange-50",  testid: "efin-outstanding-ap" },
            { label: "Gross Margin",   value: fmtMinor(margin),          sub: marginPct != null ? `${marginPct.toFixed(1)}%` : "—", icon: BarChart3, color: marginNeg ? "text-red-700" : "text-violet-700", bg: marginNeg ? "bg-red-50" : "bg-violet-50", testid: "efin-gross-margin" },
          ].map(({ label, value, sub, icon: Icon, color, bg, testid }) => (
            <div key={label} data-testid={testid} className={`rounded-lg border border-slate-100 p-3 ${bg}`}>
              <div className={`flex items-center gap-1 mb-0.5 ${color}`}>
                <Icon className="w-3.5 h-3.5" />
                <span className="text-[10px] font-mono uppercase tracking-wide">{label}</span>
              </div>
              <p className={`text-base font-bold ${color}`}>{value}</p>
              <p className="text-[10px] text-slate-500">{sub}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── E2E-04H: Closeout Readiness Panel ────────────────────────────────────────
function CloseoutReadinessPanel({ eid, navigate }) {
  const { user } = useAuth();
  const canView = hasCapability(user, "event.closeout.read") || hasCapability(user, "event.closeout.manage") || hasCapability(user, "event.ops.read");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    closeoutApi.getOverview(eid)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eid, canView]);

  if (!canView) return null;

  const gateStatus = data?.gate_status || data?.readiness_status;
  const passedGates = data?.passed_gates || data?.gates_passed || 0;
  const totalGates  = data?.total_gates  || data?.gates_total  || 0;
  const blockedGates = data?.blocked_gates || data?.gates_blocked || 0;
  const pendingEvidence = data?.pending_evidence_count ?? data?.pending_count ?? 0;
  const readinessScore  = data?.readiness_score ?? data?.score ?? null;
  const warnings        = data?.warnings || [];

  const isReady   = gateStatus === "READY" || gateStatus === "PASSED";
  const isBlocked = gateStatus === "BLOCKED" || gateStatus === "FAILED";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="closeout-readiness-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <CheckCircle2 className={`w-4 h-4 ${isReady ? "text-emerald-600" : isBlocked ? "text-red-500" : "text-amber-500"}`} />
          Closeout Readiness
        </h3>
        <button
          onClick={() => navigate(`/crm/events/${eid}/closeout`)}
          data-testid="closeout-drill-btn"
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5 font-medium border border-blue-200 px-2 py-1 rounded-lg hover:border-blue-400 transition-all"
        >
          <ExternalLink className="w-3 h-3 mr-0.5" />Open Closeout
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : !data ? (
        <div>
          <p className="text-xs text-slate-400">No closeout session started.</p>
          <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Closeout gate not yet initiated for this event.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded ${isReady ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : isBlocked ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
              {gateStatus || "PENDING"}
            </span>
            {readinessScore != null && (
              <span className="text-xs font-bold text-slate-700">{readinessScore}%</span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 text-[10px]">
            <div className="bg-emerald-50 rounded p-2 text-center">
              <p className="font-bold text-emerald-700 text-sm">{passedGates}</p>
              <p className="text-emerald-600">Passed</p>
            </div>
            <div className={`rounded p-2 text-center ${blockedGates > 0 ? "bg-red-50" : "bg-slate-50"}`}>
              <p className={`font-bold text-sm ${blockedGates > 0 ? "text-red-700" : "text-slate-400"}`}>{blockedGates}</p>
              <p className={blockedGates > 0 ? "text-red-600" : "text-slate-400"}>Blocked</p>
            </div>
            <div className={`rounded p-2 text-center ${pendingEvidence > 0 ? "bg-amber-50" : "bg-slate-50"}`}>
              <p className={`font-bold text-sm ${pendingEvidence > 0 ? "text-amber-700" : "text-slate-400"}`}>{pendingEvidence}</p>
              <p className={pendingEvidence > 0 ? "text-amber-600" : "text-slate-400"}>Pending</p>
            </div>
          </div>
          {warnings.length > 0 && (
            <div className="space-y-1">
              {warnings.slice(0, 3).map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                  <AlertTriangle className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />{w}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── E2E-04H: Acceptance Summary Panel ────────────────────────────────────────
function AcceptanceSummaryPanel({ eid }) {
  const { user } = useAuth();
  const canView = hasCapability(user, "ap.invoice.view") || hasCapability(user, "ap.acceptance.review") || hasCapability(user, "event.ops.read");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    opsSetupApi.getAcceptanceSummary(eid)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eid, canView]);

  if (!canView) return null;

  const noSubs = data?.attribution_status === "NO_SUBMISSIONS";
  const pending = data?.pending_review ?? 0;
  const accepted = data?.accepted ?? 0;
  const rejected = data?.rejected ?? 0;
  const exceptions = data?.outstanding_exceptions ?? 0;
  const total = data?.total ?? 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="acceptance-summary-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <PackageCheck className={`w-4 h-4 ${pending > 0 ? "text-amber-500" : exceptions > 0 ? "text-red-500" : "text-blue-500"}`} />
          Delivery Acceptance
        </h3>
        <button
          onClick={() => navigate(`/ap-acceptance`)}
          data-testid="acceptance-drill-btn"
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5 font-medium border border-blue-200 px-2 py-1 rounded-lg hover:border-blue-400 transition-all"
        >
          <ExternalLink className="w-3 h-3 mr-0.5" />Review
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : !data || noSubs ? (
        <div>
          <p className="text-xs text-slate-400">No delivery submissions found for this event.</p>
          <p className="text-[10px] text-slate-400 mt-1">Submissions will appear here once suppliers submit delivery evidence.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-bold text-slate-700">{total}</span> total submissions
            {pending > 0 && <span className="text-amber-600 font-medium">· {pending} pending review</span>}
          </div>
          <div className="grid grid-cols-3 gap-2 text-[10px]">
            <div className="bg-emerald-50 rounded p-2 text-center">
              <p className="font-bold text-emerald-700 text-sm">{accepted}</p>
              <p className="text-emerald-600">Accepted</p>
            </div>
            <div className={`rounded p-2 text-center ${pending > 0 ? "bg-amber-50" : "bg-slate-50"}`}>
              <p className={`font-bold text-sm ${pending > 0 ? "text-amber-700" : "text-slate-400"}`}>{pending}</p>
              <p className={pending > 0 ? "text-amber-600" : "text-slate-400"}>Pending</p>
            </div>
            <div className={`rounded p-2 text-center ${rejected > 0 || exceptions > 0 ? "bg-red-50" : "bg-slate-50"}`}>
              <p className={`font-bold text-sm ${rejected > 0 ? "text-red-700" : "text-slate-400"}`}>{rejected}</p>
              <p className={rejected > 0 ? "text-red-600" : "text-slate-400"}>Rejected</p>
            </div>
          </div>
          {exceptions > 0 && (
            <div className="flex items-center gap-1.5 text-[10px] text-red-700 bg-red-50 px-2 py-1.5 rounded border border-red-200">
              <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0" />{exceptions} outstanding exception{exceptions > 1 ? "s" : ""} require resolution before closeout
            </div>
          )}
          {/* Recent submissions */}
          {(data.submissions || []).slice(0, 3).map(s => (
            <div key={s.id} className="flex items-center justify-between text-[10px] bg-slate-50 rounded px-2 py-1.5 border border-slate-100">
              <span className="text-slate-600 font-medium truncate max-w-[100px]">{s.supplier_name || s.supplier_id || "Supplier"}</span>
              <span className={`font-mono font-semibold ${
                s.status === "ACCEPTED" ? "text-emerald-600" : s.status === "REJECTED" ? "text-red-600" : "text-amber-600"
              }`}>{s.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── E2E-04H: Sourcing Package Panel ──────────────────────────────────────────
function SourcingPackagePanel({ eid }) {
  const { user } = useAuth();
  const canView = hasCapability(user, "event.ops.read") || hasCapability(user, "sourcing.read");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    opsSetupApi.getSourcingSummary(eid)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eid, canView]);

  if (!canView) return null;

  const notAttributed = data?.attribution_status === "NOT_ATTRIBUTED";
  const empty         = data?.attribution_status === "ATTRIBUTED_EMPTY";
  const packages      = data?.packages || [];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="sourcing-package-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <MessageSquare className="w-4 h-4 text-indigo-500" />
          Sourcing Packages
        </h3>
        <button
          onClick={() => navigate(`/sourcing`)}
          data-testid="sourcing-drill-btn"
          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5 font-medium border border-indigo-200 px-2 py-1 rounded-lg hover:border-indigo-400 transition-all"
        >
          <ExternalLink className="w-3 h-3 mr-0.5" />Sourcing
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : !data || notAttributed ? (
        <div>
          <div className="flex items-center gap-1.5 text-[10px] text-amber-700 bg-amber-50 px-2 py-1.5 rounded border border-amber-200 mb-2">
            <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0" />Missing Attribution
          </div>
          <p className="text-xs text-slate-400">No sourcing opportunity linked to this event.</p>
          <p className="text-[10px] text-slate-400 mt-1">Set <code className="bg-slate-100 px-1 rounded">opportunity_id</code> on the event master to enable sourcing traceability.</p>
        </div>
      ) : empty ? (
        <div>
          <p className="text-xs text-slate-500">Opportunity <code className="bg-slate-100 px-1 rounded text-[9px]">{data.opportunity_id}</code> is linked.</p>
          <p className="text-xs text-slate-400 mt-1">No sourcing packages created yet for this opportunity.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <p className="text-[10px] text-slate-500 font-mono">{data.total} package{data.total !== 1 ? "s" : ""} via opp {(data.opportunity_id || "").slice(-8)}</p>
          {packages.slice(0, 4).map(p => (
            <div key={p.id} className="flex items-center justify-between text-[10px] bg-slate-50 rounded px-2 py-1.5 border border-slate-100">
              <span className="text-slate-700 font-medium truncate max-w-[120px]">{p.title || p.service_category_name || p.id?.slice(-8)}</span>
              <span className={`font-mono font-semibold text-[9px] ${
                p.status === "AWARDED" ? "text-emerald-600" :
                p.status === "ACTIVE" || p.status === "PUBLISHED" ? "text-blue-600" :
                "text-slate-400"
              }`}>{p.status || "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Run of Show Tab ───────────────────────────────────────────────────────────
function RunOfShowTab({ eid, canRoS }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ sequence_no: 1, title: "", planned_start_at: "", planned_end_at: "", criticality: "NORMAL" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await opsApi.listRunOfShow(eid); setItems(r.data); }
    catch { toast.error("Failed to load run of show"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await opsApi.createRosItem(eid, form);
      toast.success("Run of show item added");
      setShowCreate(false);
      setForm({ sequence_no: items.length + 2, title: "", planned_start_at: "", planned_end_at: "", criticality: "NORMAL" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add item"); }
    finally { setSaving(false); }
  };

  const transition = async (rid, target, extra = {}) => {
    try {
      await opsApi.transitionRosItem(eid, rid, { target_status: target, ...extra });
      toast.success(`→ ${target}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Transition failed"); }
  };

  const NEXT = { SCHEDULED: ["READY", "CANCELLED"], READY: ["IN_PROGRESS", "SCHEDULED"], IN_PROGRESS: ["COMPLETED", "DELAYED"], DELAYED: ["IN_PROGRESS", "SKIPPED"], COMPLETED: [], SKIPPED: [], CANCELLED: [] };
  const CRITICALITY_STYLE = { NORMAL: "", IMPORTANT: "border-l-4 border-l-amber-400", CRITICAL: "border-l-4 border-l-red-500" };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-800">Run of Show ({items.length} items)</h3>
        {canRoS && (
          <Button size="sm" variant="outline" onClick={() => setShowCreate(v => !v)} data-testid="add-ros-item-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Item
          </Button>
        )}
      </div>

      {showCreate && canRoS && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-indigo-100 p-5 space-y-3">
          <h4 className="font-medium text-slate-700 text-sm">New Run of Show Item</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Sequence #</Label>
              <Input type="number" min={1} value={form.sequence_no} onChange={e => setForm(p => ({...p, sequence_no: Number(e.target.value)}))} className="mt-1 h-8 text-sm" data-testid="ros-seq-input" />
            </div>
            <div>
              <Label className="text-xs">Criticality</Label>
              <select value={form.criticality} onChange={e => setForm(p => ({...p, criticality: e.target.value}))} className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="ros-criticality-input">
                {["NORMAL","IMPORTANT","CRITICAL"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input placeholder="e.g. Guest Registration Opens" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="ros-title-input" />
            </div>
            <div>
              <Label className="text-xs">Planned Start *</Label>
              <Input type="datetime-local" value={form.planned_start_at} onChange={e => setForm(p => ({...p, planned_start_at: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="ros-start-input" />
            </div>
            <div>
              <Label className="text-xs">Planned End *</Label>
              <Input type="datetime-local" value={form.planned_end_at} onChange={e => setForm(p => ({...p, planned_end_at: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="ros-end-input" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" size="sm" disabled={saving} data-testid="ros-create-submit">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Add Item</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No run-of-show items. {canRoS ? "Add your first item above." : ""}</p>}
        {items.map(item => (
          <div key={item.id} className={`bg-white rounded-lg border border-slate-200 p-4 ${CRITICALITY_STYLE[item.criticality] || ""}`} data-testid={`ros-item-${item.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="text-slate-400 text-sm font-mono w-6 text-right">#{item.sequence_no}</span>
                <div>
                  <span className="font-medium text-slate-800 text-sm">{item.title}</span>
                  {item.criticality !== "NORMAL" && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${item.criticality === "CRITICAL" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}>{item.criticality}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROS_STATUS_STYLE[item.status] || "bg-slate-100 text-slate-500"}`}>{item.status}</span>
                {canRoS && NEXT[item.status]?.map(t => (
                  <button key={t} onClick={() => transition(item.id, t)} className={`text-xs px-2 py-0.5 rounded border transition-colors ${t === "COMPLETED" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : t === "CANCELLED" || t === "SKIPPED" ? "border-red-200 text-red-500 hover:bg-red-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`ros-${item.id}-to-${t.toLowerCase()}`}>→{t}</button>
                ))}
              </div>
            </div>
            <div className="mt-1.5 text-xs text-slate-500 flex gap-4">
              <span>Planned: {fmt(item.planned_start_at)} — {fmt(item.planned_end_at)}</span>
              {item.actual_start_at && <span className="text-amber-600">Actual start: {fmt(item.actual_start_at)}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Streams Tab ───────────────────────────────────────────────────────────────
function StreamsTab({ eid, canManage }) {
  const [statuses, setStatuses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ss, sv] = await Promise.all([eventApi.listStreams(eid), opsApi.listStreamStatus(eid)]);
      setStreams(ss.data || []);
      setStatuses(sv.data || []);
    } catch { toast.error("Failed to load stream data"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const createStatus = async (streamId) => {
    try {
      await opsApi.createStreamStatus(eid, { execution_stream_id: streamId, mandatory_for_go_live: true });
      toast.success("Stream execution record created");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (ssid, patch) => {
    setSaving(ssid);
    try {
      await opsApi.updateStreamStatus(eid, ssid, patch);
      toast.success("Stream status updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(null); }
  };

  const statusById = Object.fromEntries(statuses.map(s => [s.execution_stream_id, s]));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Stream Execution Status</h3>
        <p className="text-xs text-slate-400">Readiness % is derived automatically</p>
      </div>
      {streams.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No execution streams defined in this event (UEIM-04).</p>}
      <div className="space-y-3">
        {streams.map(stream => {
          const ss = statusById[stream.id];
          return (
            <div key={stream.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`stream-row-${stream.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                <div>
                  <span className="font-medium text-slate-900">{stream.name}</span>
                  {stream.lead_user_id && <span className="text-xs text-slate-400 ml-2">Lead: {stream.lead_user_id}</span>}
                </div>
                {!ss && canManage && (
                  <Button size="sm" variant="outline" onClick={() => createStatus(stream.id)} data-testid={`create-stream-status-${stream.id}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Track Status
                  </Button>
                )}
                {ss && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STREAM_STATUS_STYLE[ss.operational_status] || "bg-slate-100"}`}>{ss.operational_status}</span>
                    {ss.readiness_display && <span className="text-xs font-mono text-slate-500">{ss.readiness_display}</span>}
                    {ss.mandatory_for_go_live && <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded border border-indigo-100">Mandatory</span>}
                  </div>
                )}
              </div>
              {ss && canManage && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <select
                    defaultValue={ss.operational_status}
                    onChange={e => updateStatus(ss.id, { operational_status: e.target.value })}
                    className="h-8 text-xs border border-slate-200 rounded-md px-2 col-span-1"
                    disabled={saving === ss.id}
                    data-testid={`stream-status-select-${ss.id}`}
                  >
                    {["NOT_READY","READY","EXECUTING","COMPLETED","BLOCKED","AT_RISK"].map(s => <option key={s}>{s}</option>)}
                  </select>
                  <Input placeholder="Current activity" defaultValue={ss.current_activity || ""} onBlur={e => e.target.value !== ss.current_activity && updateStatus(ss.id, { current_activity: e.target.value })} className="h-8 text-xs col-span-1" data-testid={`stream-activity-${ss.id}`} />
                  <Input placeholder="Blocker (if any)" defaultValue={ss.current_blocker || ""} onBlur={e => e.target.value !== (ss.current_blocker || "") && updateStatus(ss.id, { current_blocker: e.target.value || null })} className="h-8 text-xs col-span-1" data-testid={`stream-blocker-${ss.id}`} />
                  <div className="flex items-center gap-1.5 col-span-1">
                    <input type="checkbox" checked={ss.mandatory_for_go_live} onChange={e => updateStatus(ss.id, { mandatory_for_go_live: e.target.checked })} className="accent-indigo-600" id={`mandatory-${ss.id}`} data-testid={`stream-mandatory-${ss.id}`} />
                    <label htmlFor={`mandatory-${ss.id}`} className="text-xs text-slate-600 cursor-pointer">Mandatory for Go-Live</label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Partners Tab ──────────────────────────────────────────────────────────────
function PartnersTab({ eid, canPartner }) {
  const [arrivals, setArrivals] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ partner_assignment_id: "", expected_arrival_at: "", site_contact_name: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pa, ar] = await Promise.all([eventApi.listPartners(eid), opsApi.listPartnerArrivals(eid)]);
      setPartners(pa.data || []);
      setArrivals(ar.data || []);
    } catch { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await opsApi.createPartnerArrival(eid, form);
      toast.success("Partner arrival record created");
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const updateArrival = async (paid, patch) => {
    try {
      await opsApi.updatePartnerArrival(eid, paid, patch);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const ARRIVAL_STATUS_STYLE = { EXPECTED: "bg-slate-100 text-slate-600", ARRIVED: "bg-blue-100 text-blue-700", CHECKED_IN: "bg-indigo-100 text-indigo-700", ON_SITE: "bg-emerald-100 text-emerald-700", RELEASED: "bg-slate-100 text-slate-400", LATE: "bg-amber-100 text-amber-700", NO_SHOW: "bg-red-100 text-red-700 font-semibold" };
  const NEXT_ARRIVAL = { EXPECTED: ["ARRIVED", "LATE", "NO_SHOW"], ARRIVED: ["CHECKED_IN"], CHECKED_IN: ["ON_SITE"], ON_SITE: ["RELEASED"], LATE: ["ARRIVED", "NO_SHOW"], RELEASED: [], NO_SHOW: [] };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-800">Partner Arrivals ({arrivals.length})</h3>
        {canPartner && (
          <Button size="sm" variant="outline" onClick={() => setShowCreate(v => !v)} data-testid="add-arrival-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Track Arrival
          </Button>
        )}
      </div>

      {showCreate && canPartner && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-indigo-100 p-5 space-y-3">
          <h4 className="font-medium text-slate-700 text-sm">Track Partner Arrival</h4>
          <div>
            <Label className="text-xs">Partner Assignment *</Label>
            <select value={form.partner_assignment_id} onChange={e => setForm(p => ({...p, partner_assignment_id: e.target.value}))} required className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="arrival-partner-select">
              <option value="">Select partner assignment…</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name || p.id}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Expected Arrival *</Label>
              <Input type="datetime-local" value={form.expected_arrival_at} onChange={e => setForm(p => ({...p, expected_arrival_at: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="arrival-time-input" />
            </div>
            <div>
              <Label className="text-xs">Site Contact</Label>
              <Input placeholder="Name" value={form.site_contact_name} onChange={e => setForm(p => ({...p, site_contact_name: e.target.value}))} className="mt-1 h-8 text-sm" data-testid="arrival-contact-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid="arrival-create-submit">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Create</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {arrivals.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No partner arrivals tracked yet.</p>}
        {arrivals.map(a => (
          <div key={a.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`arrival-row-${a.id}`}>
            <div>
              <span className="font-medium text-slate-800 text-sm">{a.partner_name || a.partner_assignment_id}</span>
              <div className="text-xs text-slate-500 mt-0.5">Expected: {fmt(a.expected_arrival_at)}{a.actual_arrival_at && ` · Arrived: ${fmt(a.actual_arrival_at)}`}</div>
              {a.site_contact_name && <div className="text-xs text-slate-400">Contact: {a.site_contact_name}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ARRIVAL_STATUS_STYLE[a.arrival_status] || ""}`}>{a.arrival_status}</span>
              {canPartner && NEXT_ARRIVAL[a.arrival_status]?.map(t => (
                <button key={t} onClick={() => updateArrival(a.id, { arrival_status: t })} className={`text-xs px-2 py-0.5 rounded border ${t === "NO_SHOW" ? "border-red-200 text-red-500" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`arrival-${a.id}-to-${t.toLowerCase()}`}>→{t}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Team Tab ──────────────────────────────────────────────────────────────────
function TeamTab({ eid, canTeam }) {
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ user_id: "", expected_at: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await opsApi.listTeamCheckins(eid); setCheckins(r.data); }
    catch { toast.error("Failed to load team check-ins"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await opsApi.createTeamCheckin(eid, form);
      toast.success("Team check-in record created");
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const update = async (tcid, patch) => {
    try { await opsApi.updateTeamCheckin(eid, tcid, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const TEAM_STYLE = { EXPECTED: "bg-slate-100 text-slate-600", CHECKED_IN: "bg-blue-100 text-blue-700", ACTIVE: "bg-emerald-100 text-emerald-700", CHECKED_OUT: "bg-slate-100 text-slate-400", LATE: "bg-amber-100 text-amber-700", ABSENT: "bg-red-100 text-red-600" };
  const NEXT_TEAM = { EXPECTED: ["CHECKED_IN", "LATE", "ABSENT"], CHECKED_IN: ["ACTIVE"], ACTIVE: ["CHECKED_OUT"], LATE: ["CHECKED_IN", "ABSENT"], CHECKED_OUT: [], ABSENT: [] };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-800">Team Check-ins ({checkins.length})</h3>
        {canTeam && (
          <Button size="sm" variant="outline" onClick={() => setShowCreate(v => !v)} data-testid="add-checkin-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Check-In
          </Button>
        )}
      </div>

      {showCreate && canTeam && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-indigo-100 p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">User ID *</Label>
              <Input placeholder="user ID" value={form.user_id} onChange={e => setForm(p => ({...p, user_id: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="checkin-user-input" />
            </div>
            <div>
              <Label className="text-xs">Expected At *</Label>
              <Input type="datetime-local" value={form.expected_at} onChange={e => setForm(p => ({...p, expected_at: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="checkin-time-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid="checkin-create-submit">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Create</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {checkins.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No team check-in records yet.</p>}
        {checkins.map(c => (
          <div key={c.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`checkin-row-${c.id}`}>
            <div>
              <span className="font-medium text-slate-800 text-sm">{c.user_id}</span>
              <div className="text-xs text-slate-500 mt-0.5">Expected: {fmt(c.expected_at)}{c.check_in_at && ` · In: ${fmt(c.check_in_at)}`}{c.check_out_at && ` · Out: ${fmt(c.check_out_at)}`}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TEAM_STYLE[c.status] || ""}`}>{c.status}</span>
              {canTeam && NEXT_TEAM[c.status]?.map(t => (
                <button key={t} onClick={() => update(c.id, { status: t })} className={`text-xs px-2 py-0.5 rounded border ${t === "ABSENT" ? "border-red-200 text-red-500" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`checkin-${c.id}-to-${t.toLowerCase()}`}>→{t}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Checkpoints Tab ───────────────────────────────────────────────────────────
function CheckpointsTab({ eid, canCheckpoint, canOverride }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", checkpoint_type: "", criticality: "MEDIUM", mandatory_for_go_live: true });
  const [saving, setSaving] = useState(false);
  const [waiverModal, setWaiverModal] = useState(null);
  const [waiverReason, setWaiverReason] = useState("");

  const load = useCallback(async () => {
    try { const r = await opsApi.listCheckpoints(eid); setItems(r.data); }
    catch { toast.error("Failed to load checkpoints"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await opsApi.createCheckpoint(eid, form);
      toast.success("Checkpoint created");
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const transition = async (cid, target, extra = {}) => {
    try {
      await opsApi.transitionCheckpoint(eid, cid, { target_status: target, ...extra });
      toast.success(`Checkpoint → ${target}`);
      setWaiverModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Transition failed"); }
  };

  const NEXT_CHK = { PENDING: ["IN_PROGRESS", "WAIVED"], IN_PROGRESS: ["VERIFIED", "FAILED", "WAIVED"], FAILED: ["IN_PROGRESS"], VERIFIED: [], WAIVED: [] };

  const mandatoryCount = items.filter(i => i.mandatory_for_go_live).length;
  const verifiedCount = items.filter(i => i.mandatory_for_go_live && ["VERIFIED","WAIVED"].includes(i.status)).length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold text-slate-800">Operational Checkpoints ({items.length})</h3>
          {mandatoryCount > 0 && (
            <p className="text-xs text-slate-500 mt-0.5">{verifiedCount}/{mandatoryCount} mandatory checkpoints cleared for go-live</p>
          )}
        </div>
        {canCheckpoint && (
          <Button size="sm" variant="outline" onClick={() => setShowCreate(v => !v)} data-testid="add-checkpoint-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Checkpoint
          </Button>
        )}
      </div>

      {showCreate && canCheckpoint && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-indigo-100 p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input placeholder="e.g. AV System Tested" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="chk-title-input" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Input placeholder="e.g. AV Test, Safety Brief" value={form.checkpoint_type} onChange={e => setForm(p => ({...p, checkpoint_type: e.target.value}))} className="mt-1 h-8 text-sm" data-testid="chk-type-input" />
            </div>
            <div>
              <Label className="text-xs">Criticality</Label>
              <select value={form.criticality} onChange={e => setForm(p => ({...p, criticality: e.target.value}))} className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="chk-criticality-input">
                {["LOW","MEDIUM","HIGH","CRITICAL"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 col-span-2">
              <input type="checkbox" checked={form.mandatory_for_go_live} onChange={e => setForm(p => ({...p, mandatory_for_go_live: e.target.checked}))} id="chk-mandatory" className="accent-indigo-600" data-testid="chk-mandatory-toggle" />
              <label htmlFor="chk-mandatory" className="text-sm text-slate-700 cursor-pointer">Mandatory for Go-Live</label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid="chk-create-submit">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Create</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      {/* Waiver modal */}
      {waiverModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h4 className="font-semibold text-slate-800 mb-3">Waive Checkpoint</h4>
            <p className="text-sm text-slate-600 mb-3">Waiving <strong>{waiverModal.title}</strong>. A reason is required (event.ops.override capability).</p>
            <Textarea placeholder="Waiver reason (mandatory)" value={waiverReason} onChange={e => setWaiverReason(e.target.value)} className="text-sm" rows={3} data-testid="waiver-reason-input" />
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={() => transition(waiverModal.id, "WAIVED", { waiver_reason: waiverReason })} disabled={!waiverReason.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="waiver-confirm-btn">Confirm Waive</Button>
              <Button size="sm" variant="outline" onClick={() => { setWaiverModal(null); setWaiverReason(""); }}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No checkpoints yet. {canCheckpoint ? "Add your first checkpoint above." : ""}</p>}
        {items.map(item => (
          <div key={item.id} className={`bg-white rounded-lg border p-4 ${item.status === "FAILED" ? "border-red-200 bg-red-50/30" : "border-slate-200"}`} data-testid={`chk-row-${item.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div>
                  <span className="font-medium text-slate-800 text-sm">{item.title}</span>
                  {item.mandatory_for_go_live && <span className="ml-2 text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded border border-indigo-100">Mandatory</span>}
                  {item.checkpoint_type && <span className="ml-1 text-xs text-slate-400">({item.checkpoint_type})</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEV_STYLE[item.criticality] || ""}`}>{item.criticality}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHK_STATUS_STYLE[item.status] || ""}`} data-testid={`chk-status-${item.id}`}>{item.status}</span>
                {canCheckpoint && NEXT_CHK[item.status]?.filter(t => t !== "WAIVED").map(t => (
                  <button key={t} onClick={() => transition(item.id, t)} className={`text-xs px-2 py-0.5 rounded border transition-colors ${t === "VERIFIED" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : t === "FAILED" ? "border-red-200 text-red-600 hover:bg-red-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`chk-${item.id}-to-${t.toLowerCase()}`}>→{t}</button>
                ))}
                {canOverride && NEXT_CHK[item.status]?.includes("WAIVED") && (
                  <button onClick={() => setWaiverModal(item)} className="text-xs px-2 py-0.5 rounded border border-amber-200 text-amber-600 hover:bg-amber-50" data-testid={`chk-${item.id}-waive`}>Waive</button>
                )}
              </div>
            </div>
            {(item.evidence_note || item.waiver_reason || item.verified_at) && (
              <div className="mt-2 text-xs text-slate-500">
                {item.evidence_note && <span className="mr-3">Evidence: {item.evidence_note}</span>}
                {item.waiver_reason && <span className="mr-3 text-amber-600">Waiver: {item.waiver_reason}</span>}
                {item.verified_at && <span>Verified: {fmt(item.verified_at)}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Incidents Tab ─────────────────────────────────────────────────────────────
function IncidentsTab({ eid, canIncident }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", severity: "MEDIUM", incident_type: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await opsApi.listIncidents(eid); setIncidents(r.data); }
    catch { toast.error("Failed to load incidents"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await opsApi.createIncident(eid, form);
      toast.success("Incident raised");
      setShowCreate(false);
      setForm({ title: "", description: "", severity: "MEDIUM", incident_type: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const update = async (iid, patch) => {
    try { await opsApi.updateIncident(eid, iid, patch); toast.success("Incident updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const NEXT_INC = { OPEN: ["ACKNOWLEDGED", "RESOLVED"], ACKNOWLEDGED: ["MITIGATING", "RESOLVED"], MITIGATING: ["RESOLVED"], RESOLVED: ["CLOSED"], CLOSED: [] };
  const openCritical = incidents.filter(i => i.severity === "CRITICAL" && ["OPEN","ACKNOWLEDGED","MITIGATING"].includes(i.status)).length;
  const openHigh = incidents.filter(i => i.severity === "HIGH" && ["OPEN","ACKNOWLEDGED","MITIGATING"].includes(i.status)).length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold text-slate-800">Operational Incidents ({incidents.length})</h3>
          {(openCritical > 0 || openHigh > 0) && (
            <p className="text-xs mt-0.5">
              {openCritical > 0 && <span className="text-red-600 font-medium mr-2">{openCritical} CRITICAL open</span>}
              {openHigh > 0 && <span className="text-amber-600 font-medium">{openHigh} HIGH open</span>}
            </p>
          )}
        </div>
        {canIncident && (
          <Button size="sm" variant="outline" onClick={() => setShowCreate(v => !v)} data-testid="raise-incident-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Raise Incident
          </Button>
        )}
      </div>

      {showCreate && canIncident && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-red-100 p-5 space-y-3">
          <h4 className="font-medium text-slate-700 text-sm">Raise Operational Incident</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Title *</Label>
              <Input placeholder="Brief incident title" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} required className="mt-1 h-8 text-sm" data-testid="inc-title-input" />
            </div>
            <div>
              <Label className="text-xs">Severity</Label>
              <select value={form.severity} onChange={e => setForm(p => ({...p, severity: e.target.value}))} className="mt-1 h-8 text-sm w-full border border-slate-200 rounded-md px-2" data-testid="inc-severity-input">
                {["LOW","MEDIUM","HIGH","CRITICAL"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Input placeholder="e.g. AV Failure, Logistics" value={form.incident_type} onChange={e => setForm(p => ({...p, incident_type: e.target.value}))} className="mt-1 h-8 text-sm" data-testid="inc-type-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description *</Label>
              <Textarea placeholder="Describe the incident" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} required className="mt-1 text-sm" rows={3} data-testid="inc-desc-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} className={form.severity === "CRITICAL" ? "bg-red-600 hover:bg-red-700 text-white" : ""} data-testid="inc-create-submit">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Raise Incident</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {incidents.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No incidents raised. All clear.</p>}
        {incidents.map(inc => (
          <div key={inc.id} className={`bg-white rounded-lg border p-4 ${inc.severity === "CRITICAL" && ["OPEN","ACKNOWLEDGED","MITIGATING"].includes(inc.status) ? "border-red-300 bg-red-50/20" : "border-slate-200"}`} data-testid={`incident-row-${inc.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800 text-sm">{inc.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEV_STYLE[inc.severity] || ""}`}>{inc.severity}</span>
                  {inc.incident_type && <span className="text-xs text-slate-400">{inc.incident_type}</span>}
                </div>
                <p className="text-xs text-slate-600 mt-1">{inc.description}</p>
                <p className="text-xs text-slate-400 mt-0.5">Reported: {fmt(inc.reported_at)}{inc.resolved_at && ` · Resolved: ${fmt(inc.resolved_at)}`}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${INC_STATUS_STYLE[inc.status] || ""}`} data-testid={`inc-status-${inc.id}`}>{inc.status}</span>
                {canIncident && NEXT_INC[inc.status]?.map(t => (
                  <button key={t} onClick={() => update(inc.id, { status: t })} className={`text-xs px-2 py-0.5 rounded border transition-colors ${t === "RESOLVED" || t === "CLOSED" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`inc-${inc.id}-to-${t.toLowerCase()}`}>→{t}</button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
